/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { ComponentInterface } from '../../canvas-common/interfaces/component.interface';
import { TicketContainer, TICKET_CHANGE_EVENTS, TicketLayout } from '../tickets/TicketContainer';
import { Texture } from 'pixi.js-legacy';
import { TicketComponentHelper } from './TicketComponentHelper';
import { Ticket } from '@touchplan/common';
import { TicketUtils } from '@appCanvasCommon/utils/TicketUtils';
import { CanvasAngularLand } from 'src/app/app-data/interfaces/canvas-angular-land';
import { TicketType } from '@touchplan/common';
import { EventMode } from '@appCanvasCommon/events/event-mode.enum';
import { LayerPaths } from '@appPlanApplication/LayerPaths';

export enum COMPLETED_ALIGNMENT_SETTINGS {
	'PADDING_RIGHT_ONE_DAY' = 0,
	'PADDING_RIGHT' = 25,
	'ALIGNMENT_HEIGHT' = 110, // From the spec
	'ALIGNMENT_HEIGHT_HALF' = 55, // From the spec / 2
}

export class TicketCompletedCheck extends TicketComponentHelper implements ComponentInterface {
	constructor(texture: Texture, canvasAngularLand: CanvasAngularLand, ticketContainer: TicketContainer) {
		super(texture, canvasAngularLand, ticketContainer);

		// Dimensions came from Davina.
		this.width = 107;
		this.height = 98;

		this.alpha = 0.5;

		const data = ticketContainer.getData();
		ticketContainer.on(TICKET_CHANGE_EVENTS.CHANGED, this.onChanged);
		this.eventMode = EventMode.None;

		this.onChanged(ticketContainer);
	}

	private onChanged = (ticketContainer: TicketContainer) => {
		const data: Ticket = ticketContainer.getData();
		const layout = ticketContainer.getLayout();
		this.renderable = data.view.isActualized;

		this._positionCheckmark(data, layout);
	};

	public update(canvasAngularLand: CanvasAngularLand, ticketContainer: TicketContainer) {
		if (this.renderable && this.visible && this.active) {
			const data: Ticket = ticketContainer.getData();
			const layout: TicketLayout = ticketContainer.getLayout();
			this._positionCheckmark(data, layout);
		}
	}

	/**
	 * Spec says:
	 * Task:
	 * - Align checkmark with assumption of 110px height
	 * - Checkmark is 25px from the right-edge if duration days > 1
	 * - Checkmark is 0px from right-edge if duration days = 1
	 */
	private _positionCheckmark(data: Ticket, layout: TicketLayout): void {
		const type = TicketUtils.getTicketType(data);
		switch (type) {
			case TicketType.Milestone:
				this.x = this._getSingleDay(layout.width);
				this.y = layout.height / 2 - this.height / 2;
				break;
			case TicketType.Constraint:
				this.x = this._getSingleDay(layout.width);
				this.y = layout.height / 2 - this.height / 2;
				break;
			default: // task
				// 25 is the padding from right edge. Only applied if duration days > 1
				const padding: number =
					(data.view.liveDurationDays as number) > 1 || layout.currentLayer === LayerPaths.UI_GRANULAR_TICKETS
						? COMPLETED_ALIGNMENT_SETTINGS.PADDING_RIGHT
						: COMPLETED_ALIGNMENT_SETTINGS.PADDING_RIGHT_ONE_DAY;
				// If duration days > 1, align to the right with a padding
				// If duration days === 1, align to the center
				this.x =
					(data.view.liveDurationDays as number) > 1 || layout.currentLayer === LayerPaths.UI_GRANULAR_TICKETS
						? this._getXMultiDay(layout.width, padding)
						: this._getSingleDay(layout.width, padding);
				// Spec calls for 110px height for the alignment calculation.
				this.y = layout.height - COMPLETED_ALIGNMENT_SETTINGS.ALIGNMENT_HEIGHT_HALF - this.height / 2;
				break;
		}
	}

	private _getSingleDay(width: number, padding: number = 0): number {
		return width / 2 - this.width / 2 - padding;
	}
	/**
	 * Returns the X coordinate for a multi-day ticket
	 * @param {number} width The width of the ticket model
	 * @param {number} padding The padding of the right edge
	 */
	private _getXMultiDay(width: number, padding: number = 0): number {
		return width - this.width - padding;
	}

	public override destroy(options?) {
		this.ticket.off(TICKET_CHANGE_EVENTS.CHANGED_ISACTUALIZED, this.onChanged);
		super.destroy(options);
	}
}
